import type {
  Event as UserEvent,
  Entry as UserEntry,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import type { MangoQuery } from "rxdb"
import { useFollowedUsers } from "src/contexts"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
} from "src/utils"

/*
 * this hook returns:
 * - true if the user has glucose data
 * - false if the user has no glucose data
 * - undefined if loading
 */
export function useHasGlucoseData(): boolean | undefined {
  const { currentFollow } = useFollowedUsers()
  const accountId = currentFollow.following_account_id

  const glucoseEventSelector: MangoQuery<UserEvent> = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      { limit: 1 },
    ),
    [accountId],
  )

  const glucoseEntrySelector: MangoQuery<UserEntry> = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      { limit: 1 },
    ),
    [accountId],
  )

  const {
    result: glucoseEventResult,
    isFetching: eventIsFetching,
  } = useFindUserEvents(glucoseEventSelector)

  const {
    result: glucoseEntryResult,
    isFetching: entryIsFetching,
  } = useFindUserEntries(glucoseEntrySelector)

  return useMemo(
    () => {
      const hasData = !!(glucoseEventResult.length + glucoseEntryResult.length)

      // if true return true
      if (hasData) {
        return true
      }

      if (entryIsFetching || eventIsFetching) {
        return undefined
      }

      return false
    },
    [
      glucoseEventResult,
      glucoseEntryResult,
      eventIsFetching,
      entryIsFetching,
    ],
  )
}
